import type {
  AlloyLegacyThemeDesignTokens,
  AvatarDesignTokens,
  AvatarSizeDesignTokens,
} from '../types';
import type {
  Weave3AvatarBundleInput,
  Weave3AvatarInput,
} from '../types/weave3Types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;
type WeaveAvatar = NonNullable<WeaveTokens['component']>['avatar'];
type WeaveAvatarBundle = NonNullable<WeaveTokens['component']>['avatarbundle'];

const fontFamily = 'Artifakt Element, sans-serif';

/**
 * Bundle ring width and overlap per size, matching the legacy alloy theme. Used
 * as-is by Alloy and as the gap fallback for Weave 2, whose `avatarbundle` node
 * carries a per-size `border-width` but no `gap` — that one arrives with Weave 3.
 * Weave 2 also stops at `xl`: neither its `avatar` nor its `avatarbundle` node
 * has an `xxl` step, so that size is fallback-only there.
 */
const BUNDLE_FALLBACK = {
  xs: { 'border-width': '1px', gap: '-3px' },
  s: { 'border-width': '1px', gap: '-6px' },
  m: { 'border-width': '2px', gap: '-6px' },
  l: { 'border-width': '2px', gap: '-7px' },
  xl: { 'border-width': '4px', gap: '-10px' },
  xxl: { 'border-width': '4px', gap: '-20px' },
} as const;

export const getAvatarTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): AvatarDesignTokens => {
  const textColor = theme.tokens.colors.text.primary.value;
  const color = (background: string) => ({
    'background-color': background,
    color: textColor,
    'border-color': 'transparent',
  });

  return {
    '1': color(theme.tokens.colors.background.accent.one.subtle.value),
    '2': color(theme.tokens.colors.background.accent.two.value),
    '3': color(theme.tokens.colors.background.accent.two.subtle.value),
    '4': color(theme.tokens.colors.background.accent.four.subtle.value),
    '5': color(theme.tokens.colors.background.accent.three.subtle.value),
    '6': color(theme.tokens.colors.background.accent.five.subtler.value),
    '7': color(theme.tokens.colors.background.accent.five.subtle.value),
    _: {
      'border-radius': theme.borderRadius.circle,
      'border-width': '0',
      'border-style': 'none',
      'font-family': theme.typography.bodyMedium.fontFamily,
      'font-weight': theme.typography.bodyMedium.fontWeight,
    },
    group: {
      'border-color': theme.tokens.colors.border.inverse.value,
    },
    xs: { 'font-size': '11px', 'line-height': '16px', ...BUNDLE_FALLBACK.xs },
    s: { 'font-size': '12px', 'line-height': '18px', ...BUNDLE_FALLBACK.s },
    m: { 'font-size': '16px', 'line-height': '20px', ...BUNDLE_FALLBACK.m },
    l: { 'font-size': '24px', 'line-height': '30px', ...BUNDLE_FALLBACK.l },
    xl: { 'font-size': '32px', 'line-height': '38px', ...BUNDLE_FALLBACK.xl },
    xxl: { 'font-size': '56px', 'line-height': '64px', ...BUNDLE_FALLBACK.xxl },
    default: {
      'background-color':
        theme.tokens.colors.background.accent.six.subtler.value,
      color: theme.tokens.colors.icon.tertiary.value,
      'border-color': 'transparent',
    },
    truncate: {
      cursor: 'pointer',
      'background-color':
        theme.tokens.colors.background.accent.six.subtler.value,
      color: textColor,
      'border-color': 'transparent',
      ':hover': {
        color: theme.tokens.colors.icon.primary.value,
        'background-color': theme.tokens.colors.background.hover.value,
      },
    },
  };
};

export const getAvatarTokensWeave = (
  tokens: WeaveTokens,
): AvatarDesignTokens => {
  const avatar: WeaveAvatar = tokens?.component?.avatar;
  const bundle: WeaveAvatarBundle = tokens?.component?.avatarbundle;

  const color = (slot?: { 'background-color'?: string }) => ({
    'background-color': slot?.['background-color'],
    color: avatar?.['text-color'],
    'border-color': 'transparent',
  });

  const size = (
    sizeTokens: { 'font-size'?: string; 'line-height'?: string } | undefined,
    borderWidth: string | undefined,
    fallback: (typeof BUNDLE_FALLBACK)[keyof typeof BUNDLE_FALLBACK],
  ): AvatarSizeDesignTokens => ({
    'font-size': sizeTokens?.['font-size'],
    'line-height': sizeTokens?.['line-height'],
    'border-width': borderWidth ?? fallback['border-width'],
    gap: fallback.gap,
  });

  return {
    '1': color(avatar?.['1']),
    '2': color(avatar?.['2']),
    '3': color(avatar?.['3']),
    '4': color(avatar?.['4']),
    '5': color(avatar?.['5']),
    '6': color(avatar?.['6']),
    '7': color(avatar?.['7']),
    _: {
      'border-radius': avatar?.['border-radius'],
      'border-width': '0',
      'border-style': 'none',
      'font-family': fontFamily,
      'font-weight': '400',
    },
    group: {
      'border-color': bundle?.['border-color'],
    },
    xs: size(avatar?.xs, bundle?.xs?.['border-width'], BUNDLE_FALLBACK.xs),
    s: size(avatar?.s, bundle?.s?.['border-width'], BUNDLE_FALLBACK.s),
    m: size(avatar?.m, bundle?.m?.['border-width'], BUNDLE_FALLBACK.m),
    l: size(avatar?.l, bundle?.l?.['border-width'], BUNDLE_FALLBACK.l),
    xl: size(avatar?.xl, bundle?.xl?.['border-width'], BUNDLE_FALLBACK.xl),
    xxl: size(undefined, undefined, BUNDLE_FALLBACK.xxl),
    default: {
      'background-color': bundle?.truncate?.['background-color'],
      color: tokens?.semantic?.['icon-color']?.medium,
      'border-color': 'transparent',
    },
    truncate: {
      cursor: 'pointer',
      'background-color': bundle?.truncate?.['background-color'],
      color: bundle?.truncate?.['text-color'],
      'border-color': 'transparent',
      ':hover': {
        color: undefined,
        'background-color': undefined,
      },
    },
  };
};

/**
 * Weave 3 colours each of the seven avatar slots individually — background,
 * initials and a subtle border all come from the same hue — where Alloy and
 * Weave 2 tint only the background and share one text colour. Ring width and
 * overlap are per-size bundle tokens here too, so the group layout stops being
 * hardcoded in the component. The W3 tree has no `border-radius`, `border-style`
 * or per-size `line-height`: an avatar is a circle by definition, its border is
 * solid, and its initials are centred by the flex box rather than by leading.
 */
export const getAvatarTokensWeave3 = (
  avatar?: Weave3AvatarInput,
  bundle?: Weave3AvatarBundleInput,
): AvatarDesignTokens => {
  const color = (slot?: {
    'background-color'?: string;
    'text-color'?: string;
    'border-color'?: string;
  }) => ({
    'background-color': slot?.['background-color'],
    color: slot?.['text-color'],
    'border-color': slot?.['border-color'],
  });

  const size = (
    sizeTokens?: { 'font-size'?: string },
    bundleTokens?: { 'border-width'?: string; gap?: string },
  ): AvatarSizeDesignTokens => ({
    'font-size': sizeTokens?.['font-size'],
    'border-width': bundleTokens?.['border-width'],
    gap: bundleTokens?.gap,
  });

  return {
    '1': color(avatar?.['1']),
    '2': color(avatar?.['2']),
    '3': color(avatar?.['3']),
    '4': color(avatar?.['4']),
    '5': color(avatar?.['5']),
    '6': color(avatar?.['6']),
    '7': color(avatar?.['7']),
    _: {
      'border-radius': '50%',
      'border-width': '1px',
      'border-style': 'solid',
      'font-family': fontFamily,
      'font-weight': avatar?.['font-weight'],
    },
    group: {
      'border-color': bundle?.['border-color'],
    },
    xs: size(avatar?.xs, bundle?.xs),
    s: size(avatar?.s, bundle?.s),
    m: size(avatar?.m, bundle?.m),
    l: size(avatar?.l, bundle?.l),
    xl: size(avatar?.xl, bundle?.xl),
    xxl: size(avatar?.xxl, bundle?.xll),
    default: {
      'background-color': avatar?.['background-color'],
      color: avatar?.icon?.fill,
      'border-color': 'transparent',
    },
    truncate: {
      cursor: 'pointer',
      'background-color': bundle?.truncate?.['background-color'],
      color: avatar?.['text-color'],
      'border-color': 'transparent',
      ':hover': {
        color: undefined,
        'background-color': undefined,
      },
    },
  };
};
